/**
 * Shared auth interceptor for all axios instances.
 *
 * Attaches Bearer token to requests and handles 401 responses with
 * silent token refresh + request queuing. All axios clients share
 * one refresh state so concurrent 401s from different services
 * don't trigger multiple refresh attempts.
 *
 * Refresh-failure classification:
 *   - **Permanent** (refresh token genuinely invalid / no token / 400/401/403
 *     from /auth/refresh): the rejection carries `err.permanent = true` and
 *     the interceptor below treats it as a real logout.
 *   - **Transient** (network blip, 502/503/504, timeout, no response at all):
 *     retried up to REFRESH_MAX_RETRIES with exponential backoff. Only when
 *     ALL retries fail does the promise reject — and even then, without the
 *     `permanent` flag, so the interceptor keeps the user logged in.
 */
import axios from 'axios';
import { getAccessToken, getRefreshToken, setTokens, clearTokens, captureReturnTo } from './auth';

const API_BASE_URL = (window.__RUNTIME_CONFIG__ || {}).REACT_APP_API_BASE_URL || process.env.REACT_APP_API_BASE_URL;
const REFRESH_URL = `${API_BASE_URL}/api/auth/refresh`;

const REFRESH_TIMEOUT_MS = 15000;        // hard cap so refresh can't hang forever
const REFRESH_MAX_RETRIES = 2;            // 1 original + 2 retries = 3 attempts total
const REFRESH_RETRY_BASE_MS = 700;        // backoff: 700ms, 1400ms

// Shared state across ALL axios instances
let isRefreshing = false;
let refreshQueue = [];

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Refresh-endpoint responses that mean "this user's session is genuinely
// over" — refresh token was rejected. Everything else (network errors,
// 5xx, timeouts) is treated as transient and retried.
const isPermanentRefreshError = (err) => {
    const status = err?.response?.status;
    return status === 400 || status === 401 || status === 403;
};

const performRefreshWithRetry = async (refreshToken, attempt = 0) => {
    try {
        const res = await axios.post(
            REFRESH_URL,
            { refresh_token: refreshToken },
            { timeout: REFRESH_TIMEOUT_MS },
        );
        setTokens(res.data.access_token, res.data.refresh_token);
    } catch (err) {
        if (isPermanentRefreshError(err)) {
            // Don't retry — refresh token is genuinely invalid
            err.permanent = true;
            throw err;
        }
        if (attempt < REFRESH_MAX_RETRIES) {
            await sleep(REFRESH_RETRY_BASE_MS * Math.pow(2, attempt));
            return performRefreshWithRetry(refreshToken, attempt + 1);
        }
        // All retries exhausted on transient error — DON'T mark as permanent
        // so the interceptor keeps the user logged in.
        throw err;
    }
};

/**
 * Attempt to refresh the access token. Only one refresh runs at a time —
 * concurrent callers are queued and resolved/rejected together.
 */
const doRefresh = () => {
    if (isRefreshing) {
        return new Promise((resolve, reject) => {
            refreshQueue.push({ resolve, reject });
        });
    }

    isRefreshing = true;
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
        isRefreshing = false;
        const err = new Error('No refresh token');
        err.permanent = true;
        return Promise.reject(err);
    }

    return performRefreshWithRetry(refreshToken)
        .then(() => {
            refreshQueue.forEach(({ resolve: r }) => r());
            refreshQueue = [];
        })
        .catch((err) => {
            refreshQueue.forEach(({ reject: r }) => r(err));
            refreshQueue = [];
            throw err;
        })
        .finally(() => {
            isRefreshing = false;
        });
};

/**
 * Attach auth request/response interceptors to an axios instance.
 * Call this once per axios instance at creation time.
 */
export const attachAuthInterceptor = (axiosInstance) => {
    // Request: attach Bearer token
    axiosInstance.interceptors.request.use((config) => {
        const token = getAccessToken();
        if (token) config.headers.Authorization = `Bearer ${token}`;
        return config;
    });

    // Response: on 401, attempt refresh then retry
    axiosInstance.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config;

            // Auth endpoints' 401s are legitimate "wrong credentials" / "bad
            // refresh token" responses — they MUST propagate to the caller
            // (LoginPage's catch) without redirect-or-refresh. Without this
            // skip, a failed login would hit the refresh path below, fail
            // (no refresh token), and trigger window.location.href = '/login'
            // — reloading the page and wiping the user's typed credentials.
            const url = originalRequest?.url || '';
            const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/refresh');

            if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
                originalRequest._retry = true;

                try {
                    await doRefresh();
                    // Retry with new token
                    originalRequest.headers.Authorization = `Bearer ${getAccessToken()}`;
                    return axiosInstance(originalRequest);
                } catch (refreshErr) {
                    // Only log the user out when refresh failed PERMANENTLY
                    // (refresh token genuinely invalid). Transient failures
                    // — network blips, 5xx, timeouts — leave the session
                    // alone; the next request will trigger another refresh
                    // attempt and the user keeps working when the network
                    // recovers.
                    if (refreshErr?.permanent) {
                        captureReturnTo();
                        clearTokens();
                        window.location.href = '/login';
                    }
                    return Promise.reject(error);
                }
            }

            return Promise.reject(error);
        }
    );
};

/**
 * Expose isRefreshing and doRefresh for the proactive refresh listener.
 */
export const getIsRefreshing = () => isRefreshing;
export { doRefresh };
